import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, DollarSign, Zap, Calendar } from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

type TimeRange = "today" | "week" | "month";

// Feature name translation
const FEATURE_NAMES: Record<string, string> = {
    content_strategy: "內容策略分析",
    ai_overview_simulation: "AI Overview 模擬",
    serp_paa: "PAA 問題抓取",
    serp_ai_overview: "AI Overview 抓取",
    serp_trends: "關鍵字趨勢",
    serp_organic: "SERP 搜尋",
    llm_citation: "LLM 引用監控",
};

function getDateRange(range: TimeRange): { start: string; end: string } {
    const now = new Date();
    const end = new Date(now);
    end.setDate(end.getDate() + 1); // Include today

    let start: Date;
    switch (range) {
        case "today":
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
        case "week":
            start = new Date(now);
            start.setDate(start.getDate() - 7);
            break;
        case "month":
            start = new Date(now);
            start.setMonth(start.getMonth() - 1);
            break;
    }

    return {
        start: start.toISOString().split("T")[0],
        end: end.toISOString().split("T")[0],
    };
}

export default function ApiUsageStats() {
    const [timeRange, setTimeRange] = useState<TimeRange>("month");
    const { start, end } = getDateRange(timeRange);

    const { data, isLoading, error } = trpc.apiUsage.getStats.useQuery(
        { startDate: start, endDate: end },
        { refetchOnWindowFocus: false }
    );

    const formatUSD = (amount: number) => {
        return `$${amount.toFixed(4)}`;
    };

    return (
        <div className="space-y-6">
            {/* Time Range Selector */}
            <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">時間區間：</span>
                <div className="flex gap-1">
                    {(["today", "week", "month"] as TimeRange[]).map((range) => (
                        <Button
                            key={range}
                            variant={timeRange === range ? "default" : "outline"}
                            size="sm"
                            onClick={() => setTimeRange(range)}
                        >
                            {range === "today" ? "今天" : range === "week" ? "本週" : "本月"}
                        </Button>
                    ))}
                </div>
            </div>

            {isLoading && (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            )}

            {error && (
                <div className="text-red-500 text-center py-4">
                    載入失敗：{error.message}
                </div>
            )}

            {data && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardDescription className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    API Token 費用
                                </CardDescription>
                                <CardTitle className="text-2xl">
                                    {formatUSD(data.totalApiCost)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-xs text-muted-foreground">
                                    OpenAI / Gemini / SERP 原始 API 費用
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardDescription className="flex items-center gap-2">
                                    <Zap className="h-4 w-4" />
                                    系統服務費 (20%)
                                </CardDescription>
                                <CardTitle className="text-2xl">
                                    {formatUSD(data.totalServiceFee)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-xs text-muted-foreground">
                                    基於 API Token 費用的 20% 服務費
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Usage Detail Table */}
                    <Card>
                        <CardHeader>
                            <CardTitle>使用量明細</CardTitle>
                            <CardDescription>
                                依功能分類的 API 使用統計
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {data.byFeature.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    此時段內無 API 使用記錄
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>功能</TableHead>
                                            <TableHead className="text-right">次數</TableHead>
                                            <TableHead className="text-right">Tokens</TableHead>
                                            <TableHead className="text-right">API 費用</TableHead>
                                            <TableHead className="text-right">服務費</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {data.byFeature.map((item) => (
                                            <TableRow key={item.feature}>
                                                <TableCell className="font-medium">
                                                    {FEATURE_NAMES[item.feature] || item.feature}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {item.requestCount}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {item.totalTokens > 0 ? item.totalTokens.toLocaleString() : "-"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {formatUSD(item.apiCost)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {formatUSD(item.serviceFee)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Totals Row */}
                                        <TableRow className="font-bold border-t-2">
                                            <TableCell>合計</TableCell>
                                            <TableCell className="text-right">
                                                {data.byFeature.reduce((sum, f) => sum + f.requestCount, 0)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {data.byFeature.reduce((sum, f) => sum + f.totalTokens, 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatUSD(data.totalApiCost)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatUSD(data.totalServiceFee)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
